import React from 'react';
import { LucideIcon, Activity, Users, Calendar, Gift, ClipboardList, MessageSquare, FileText, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface NoDataPlaceholderProps {
    icon?: LucideIcon;
    title?: string;
    description?: string;
    className?: string;
    iconClassName?: string;
    variant?: 'default' | 'activities' | 'users' | 'events' | 'coupons' | 'surveys' | 'notifications' | 'reports' | 'settings';
}

export const NoDataPlaceholder: React.FC<NoDataPlaceholderProps> = ({
    icon: Icon,
    title,
    description,
    className = "",
    iconClassName = "text-gray-400",
    variant = 'default'
}) => {
    const { t } = useTranslation();

    // Predefined variants
    const variants = {
        activities: {
            icon: Activity,
            title: t('ui.no_activities_yet'),
            description: t('ui.activities_will_appear_here')
        },
        users: {
            icon: Users,
            title: t('ui.no_users_found'),
            description: t('ui.user_data_will_appear_here')
        },
        events: {
            icon: Calendar,
            title: t('ui.no_events_available'),
            description: t('ui.events_will_be_displayed_here')
        },
        coupons: {
            icon: Gift,
            title: t('ui.no_coupons_available'),
            description: t('ui.coupon_data_will_appear_here')
        },
        surveys: {
            icon: ClipboardList,
            title: t('ui.no_surveys_found'),
            description: t('ui.survey_data_will_be_displayed_here')
        },
        notifications: {
            icon: MessageSquare,
            title: t('ui.no_notifications'),
            description: t('ui.notifications_will_appear_here')
        },
        reports: {
            icon: FileText,
            title: t('ui.no_reports_available'),
            description: t('ui.report_data_will_be_displayed_here')
        },
        settings: {
            icon: Settings,
            title: t('ui.no_settings_data'),
            description: t('ui.settings_information_will_appear_here')
        },
        default: {
            icon: undefined,
            title: t('ui.no_data_available'),
            description: t('ui.nothing_to_display')
        }
    };

    const selectedVariant = variants[variant];
    const finalIcon = Icon || selectedVariant.icon;
    const finalTitle = title || selectedVariant.title;
    const finalDescription = description || selectedVariant.description;

    return (
        <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
            {finalIcon && (
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    {React.createElement(finalIcon, { className: `w-8 h-8 ${iconClassName}` })}
                </div>
            )}
            <div className="text-gray-500 dark:text-gray-400 text-center">
                <div className="text-lg font-medium mb-2 dark:text-gray-300">{finalTitle}</div>
                <div className="text-sm dark:text-gray-400">{finalDescription}</div>
            </div>
        </div>
    );
}; 